<script setup lang="ts">
  import {
    Checkbox,
    CheckboxSize,
    Chip,
    ChipSize,
    Icon,
    IconName,
    IconSize,
    Radio,
    RadioSize,
    Typo,
    TypoVariant,
  } from '@travelata-libs/ui-kit';
  import { setBodyOverflow } from '@travelata-libs/utils';
  import { computed, nextTick, ref } from 'vue';
  import SerpFilterTooltipSheet from './SerpFilterTooltipSheet.vue';
  import type { SerpFilterGroupId, SerpFilterOptionView, SerpOptionsFilterGroupView } from '../model/types';

  interface SerpFilterGroupProps {
    group: SerpOptionsFilterGroupView;
    titleHidden?: boolean;
  }

  const props = withDefaults(defineProps<SerpFilterGroupProps>(), { titleHidden: false });

  interface SerpFilterGroupEmits {
    (e: 'select', groupId: SerpFilterGroupId, key: string): void;
    (e: 'reset', groupId: SerpFilterGroupId): void;
  }

  const emit = defineEmits<SerpFilterGroupEmits>();

  const RESET_VALUE = '';
  const RESET_TESTID_KEY = 'reset';

  const isTooltipOpen = ref<boolean>(false);

  // Chip прокидывает клик на корневой элемент даже задизейбленным
  const onSelect = (option: SerpFilterOptionView): void => {
    if (option.disabled) return;
    emit('select', props.group.id, option.key);
  };

  const onRadioChange = (value: string): void => {
    if (value === RESET_VALUE) {
      emit('reset', props.group.id);
      return;
    }

    emit('select', props.group.id, value);
  };

  const radioValue = computed<string>(() => props.group.options.find((option) => option.selected)?.key ?? RESET_VALUE);

  const radioOptions = computed<SerpFilterOptionView[]>(() => {
    if (!props.group.resetLabel) return props.group.options;

    const resetOption: SerpFilterOptionView = {
      key: RESET_VALUE,
      label: props.group.resetLabel,
      selected: radioValue.value === RESET_VALUE,
      disabled: false,
    };

    return [resetOption, ...props.group.options];
  });

  const hasDescriptions = computed<boolean>(() => props.group.options.some((option) => Boolean(option.description)));

  const isListControl = computed<boolean>(() => props.group.control === 'checkboxes');

  const checkboxOptions = computed<SerpFilterOptionView[]>(() => {
    if (!isListControl.value || !props.group.resetLabel) return props.group.options;

    const resetOption: SerpFilterOptionView = {
      key: RESET_VALUE,
      label: props.group.resetLabel,
      selected: props.group.options.every((option) => !option.selected),
      disabled: false,
    };

    return [resetOption, ...props.group.options];
  });

  const onCheckboxChange = (option: SerpFilterOptionView): void => {
    if (option.key !== RESET_VALUE) {
      onSelect(option);
      return;
    }

    if (!option.selected) emit('reset', props.group.id);
  };

  const hasTail = computed<boolean>(() => Boolean(props.group.appendIcon || props.group.tooltip));

  const optionTestId = (option: SerpFilterOptionView): string =>
    `serp-filter-option-${props.group.id}-${option.key || RESET_TESTID_KEY}`;

  const openTooltip = (): void => {
    isTooltipOpen.value = true;
  };

  const closeTooltip = async (): Promise<void> => {
    isTooltipOpen.value = false;
    await nextTick();
    setBodyOverflow('hidden');
  };
</script>

<template>
  <section
    :class="['serp-filter-group', { 'serp-filter-group_list': isListControl }]"
    :data-testid="`serp-filter-group-${group.id}`"
  >
    <div
      v-if="group.control !== 'checkbox' && !titleHidden"
      class="serp-filter-group__header"
    >
      <Typo
        class="serp-filter-group__title"
        :variant="TypoVariant.BodyLRegular"
        tag="p"
      >
        {{ group.title }}
      </Typo>

      <span
        v-if="hasTail"
        class="serp-filter-group__tail"
      >
        <Icon
          v-if="group.appendIcon"
          class="serp-filter-group__append-icon"
          :name="group.appendIcon"
          :size="IconSize.M"
          :data-testid="`serp-filter-append-icon-${group.id}`"
        />
        <button
          v-if="group.tooltip"
          type="button"
          class="serp-filter-group__tooltip-button"
          :aria-label="group.tooltip.title"
          :data-testid="`serp-filter-tooltip-${group.id}`"
          @click.prevent.stop="openTooltip"
          @keydown.stop
        >
          <Icon
            :name="IconName.Question"
            :size="IconSize.M"
          />
        </button>
      </span>
    </div>

    <div
      v-if="group.control === 'chips'"
      class="serp-filter-group__chips"
    >
      <Chip
        v-for="option in group.options"
        :key="option.key"
        class="serp-filter-group__chip"
        rounded
        :size="ChipSize.M"
        :active="option.selected"
        :disabled="option.disabled"
        :data-testid="optionTestId(option)"
        @click="onSelect(option)"
      >
        <template #prepend>
          <slot
            name="option-prepend"
            :option="option"
          >
            <Icon
              v-if="group.optionIcon && option.key !== RESET_VALUE"
              class="serp-filter-group__option-icon"
              :name="group.optionIcon"
              :size="IconSize.M"
              :style="option.selected ? undefined : { fill: group.optionIconColor }"
              :data-testid="`serp-filter-option-icon-${group.id}-${option.key}`"
            />
          </slot>
        </template>

        <slot
          name="option-label"
          :option="option"
        >
          {{ option.label }}
        </slot>

        <template #append>
          <slot
            name="option-append"
            :option="option"
          />
        </template>
      </Chip>
    </div>

    <div
      v-else-if="group.control === 'radio'"
      :class="[
        'serp-filter-group__options',
        {
          'serp-filter-group__options_spaced': hasDescriptions,
        },
      ]"
    >
      <Radio
        v-for="option in radioOptions"
        :key="option.key"
        class="serp-filter-group__option"
        :model-value="radioValue"
        :value="option.key"
        :name="`serp-filter-${group.id}`"
        :size="RadioSize.L"
        :mobile="true"
        :align="hasDescriptions ? 'flex-start' : 'center'"
        :disabled="option.disabled"
        :data-testid="optionTestId(option)"
        @update:modelValue="onRadioChange"
      >
        <span class="serp-filter-group__option-row">
          <slot
            name="option-prepend"
            :option="option"
          >
            <Icon
              v-if="group.optionIcon && option.key !== RESET_VALUE"
              class="serp-filter-group__option-icon"
              :name="group.optionIcon"
              :size="IconSize.M"
              :style="{ fill: group.optionIconColor }"
              :data-testid="`serp-filter-option-icon-${group.id}-${option.key}`"
            />
          </slot>

          <span class="serp-filter-group__option-content">
            <slot
              name="option-label"
              :option="option"
            >
              <Typo
                :variant="TypoVariant.BodyLRegular"
                tag="span"
              >
                {{ option.label }}
              </Typo>
            </slot>
            <Typo
              v-if="option.description"
              class="serp-filter-group__option-description"
              :variant="TypoVariant.BodyMRegular"
              tag="span"
            >
              {{ option.description }}
            </Typo>
          </span>

          <slot
            name="option-append"
            :option="option"
          />
        </span>
      </Radio>
    </div>

    <div
      v-else
      :class="['serp-filter-group__options', { 'serp-filter-group__options_spaced': isListControl }]"
    >
      <Checkbox
        v-for="(option, index) in checkboxOptions"
        :key="option.key"
        class="serp-filter-group__option"
        :model-value="option.selected"
        :disabled="option.disabled"
        :size="CheckboxSize.L"
        :mobile="true"
        :data-testid="optionTestId(option)"
        @update:modelValue="onCheckboxChange(option)"
      >
        <span class="serp-filter-group__option-row">
          <slot
            name="option-prepend"
            :option="option"
          >
            <Icon
              v-if="group.optionIcon && option.key !== RESET_VALUE"
              class="serp-filter-group__option-icon"
              :name="group.optionIcon"
              :size="IconSize.M"
              :style="{ fill: group.optionIconColor }"
              :data-testid="`serp-filter-option-icon-${group.id}-${option.key}`"
            />
          </slot>

          <span class="serp-filter-group__option-content">
            <span class="serp-filter-group__option-label">
              <slot
                name="option-label"
                :option="option"
              >
                <Typo
                  :variant="TypoVariant.BodyLRegular"
                  tag="span"
                >
                  {{ option.label }}
                </Typo>
              </slot>

              <span
                v-if="hasTail && index === 0"
                class="serp-filter-group__tail"
              >
                <Icon
                  v-if="group.appendIcon"
                  class="serp-filter-group__append-icon"
                  :name="group.appendIcon"
                  :size="IconSize.M"
                  :data-testid="`serp-filter-append-icon-${group.id}`"
                />
                <button
                  v-if="group.tooltip"
                  type="button"
                  class="serp-filter-group__tooltip-button"
                  :aria-label="group.tooltip.title"
                  :data-testid="`serp-filter-tooltip-${group.id}`"
                  @click.prevent.stop="openTooltip"
                  @keydown.stop
                >
                  <Icon
                    :name="IconName.Question"
                    :size="IconSize.M"
                  />
                </button>
              </span>
            </span>
            <Typo
              v-if="option.description"
              class="serp-filter-group__option-description"
              :variant="TypoVariant.BodyMRegular"
              tag="span"
            >
              {{ option.description }}
            </Typo>
          </span>

          <slot
            name="option-append"
            :option="option"
          />
        </span>
      </Checkbox>
    </div>

    <SerpFilterTooltipSheet
      v-if="isTooltipOpen && group.tooltip"
      :title="group.tooltip.title"
      :text="group.tooltip.text"
      @close="closeTooltip"
    />
  </section>
</template>

<style scoped lang="scss">
  .serp-filter-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);

    &_list {
      gap: var(--spacing-s);
    }

    &__header {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxs);
      min-width: 0;
    }

    &__title {
      color: var(--color-text-primary);
      display: block;
      margin: 0;
    }

    &__tail {
      align-items: center;
      display: inline-flex;
      flex-shrink: 0;
      gap: var(--spacing-xxs);
    }

    &__append-icon {
      fill: var(--color-green-50);
      flex-shrink: 0;
    }

    &__option-icon {
      flex-shrink: 0;
    }

    &__tooltip-button {
      align-items: center;
      background: none;
      border: 0;
      cursor: pointer;
      display: inline-flex;
      fill: var(--color-text-secondary);
      flex-shrink: 0;
      margin: 0;
      padding: 0;
    }

    &__chips {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-xs);
    }

    &__chip {
      cursor: pointer;
    }

    &__options {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);

      &_spaced {
        gap: var(--spacing-m);
      }
    }

    &__option-row {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
      min-width: 0;
      width: 100%;
    }

    &__option-content {
      display: flex;
      flex: 0 1 auto;
      flex-direction: column;
      gap: var(--spacing-xxs);
      min-width: 0;

      // ui-kit держит .tl-radio__content в nowrap
      white-space: normal;
    }

    &__option-label {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxs);
      min-width: 0;
    }

    &__option-description {
      color: var(--color-text-secondary);
    }
  }
</style>
